/* eslint-disable functional/immutable-data */
import React from 'react'
import { Downgraded, useHookstate } from '@hookstate/core';
import { Grid, Typography } from '@mui/material';
import { DataGrid, gridClasses } from '@mui/x-data-grid';
import { Controller } from 'react-hook-form';
import { useLang } from '~/hooks/useLang';
import notify from '~/utils/notify';
import FilterLine from './components/FilterLine';
import { ptBR } from '@mui/x-data-grid/locales';

export default function CrudTableField({ 
  register, 
  model, 
  getValues, 
  control, 
  placeholder, 
  columns, 
  lines, 
  filterModel, 
  rows, 
  required = false,
  validate = () => {return true},
  columnGroup = [],
  lockFields = false,
  hideTitle = false
}) {
  const { translate } = useLang();
  const defaultValue = useHookstate([])
  const result = validate()
  const onChangeRef = React.useRef<((value: any) => void) | null>(null)
  const notifiedRef = React.useRef(false)

  function addButton(value: number | readonly number[] | null){
    const ids = Array.isArray(value) ? value : (value != null ? [value] : [])

    ids.forEach((id) => {
      const alreadyExist = rows.get().find(item => item.id == id)

      if(!alreadyExist){
        const newRow = lines.find(item => item.id == id)
        if (newRow) {
          rows.merge([newRow])
          defaultValue.set(rows.get())
        }
      } else {
        const existingRow = rows.get().find(item => item.id == id)
        const itemName = existingRow?.name ?? String(id)
        notify({ message: `${translate('The item')} "${itemName}" ${translate('has already been added.')}`, type: 'error' })
      }
    })

    if (onChangeRef.current) {
      const formatted = rows.get().map((r: any) => ({
        id: r.id,
        required: r.required ? 1 : 0,
      }))
      onChangeRef.current(formatted.length > 0 ? formatted : [])
    }
  }

  const handleProcessRowUpdate = (newRow, oldRow) => {
    const updatedRows = rows.get().map(row => 
      row.id === newRow.id ? newRow : row
    )
    rows.set(updatedRows)
    defaultValue.set(updatedRows)
    return newRow
  }

  return (
    <>
      {!hideTitle && (
        <Grid item xs={12}>
          <Typography variant='subtitle1'>
            {translate(model)}
            {required ? '*' : ''}
          </Typography>
          {filterModel && <FilterLine filter={filterModel} addButton={addButton} lockFields={lockFields}/>}
        </Grid>
      )}

    <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'row' }}>
      <Controller
      {...register(`${model}`)}
      name={model}
      control={control}
      defaultValue={defaultValue.get()}
      rules={{ validate: validate }}
      render={(props) => {
        onChangeRef.current = props.field.onChange
        if (props.fieldState.error && result !== true) {
          if (!notifiedRef.current) {
            notifiedRef.current = true
            notify({ message: props.fieldState.error.message ?? translate('Fill in this field.'), type: 'error' })
          }
        } else {
          notifiedRef.current = false
        }
        return (
        <DataGrid 
        localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
        columns={columns} 
        columnVisibilityModel={{
          id: false
        }}
        columnGroupingModel={columnGroup}
        rows={rows.attach(Downgraded).get()} 
        processRowUpdate={handleProcessRowUpdate}
        autoHeight 
        sx={{ 
          minHeight: '250px', 
          [`.${gridClasses.columnHeaderTitle}`]: {
            fontWeight: 'bold !important',
          },
        }} 
        hideFooterPagination={true}
        disableColumnMenu
        disableColumnSorting
        />
      )
      }}
      />
    </Grid>
    </>
  )
}